import { useCallback, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';
import { AuthContext, type AuthContextValue } from '@/lib/auth/auth-context';
import { authReducer, initialAuthState } from '@/lib/auth/auth-reducer';
import {
  refreshSession,
  restoreSession,
  signIn as cognitoSignIn,
  signInErrorMessage,
  signOut as cognitoSignOut,
} from '@/lib/auth/cognito';
import { isTokenExpired } from '@/lib/auth/token';
import { setAuthToken, setUnauthorizedHandler } from '@/lib/api';

/**
 * Holds the operator's Cognito session for the whole app and keeps
 * `src/lib/api` supplied with a token that is still good.
 *
 * Three things happen here that a capture client on a phone depends on.
 * On mount it restores the stored session, so a reload or a relaunch from
 * the home screen does not bounce a signed-in operator to sign-in. It
 * registers the 401 handler `src/lib/api/client.ts` calls, which refreshes
 * once and signs out when refresh fails. It refreshes on return from
 * background when the token is inside its expiry skew, which is the common
 * case after a phone has been in a pocket between aisles.
 *
 * No credential is logged here: not the password, not the ID token, not
 * the refresh token, which this component never holds at all.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);

  // The token the API client reads is set imperatively rather than from an
  // effect on `state`, so a refresh completed inside the 401 handler is
  // visible to the retry in the same tick.
  useEffect(() => {
    setAuthToken(state.idToken);
  }, [state.idToken]);

  useEffect(() => {
    let cancelled = false;
    restoreSession()
      .then((session) => {
        if (cancelled) return;
        if (session) {
          dispatch({ type: 'session-restored', session, nowMs: Date.now() });
        } else {
          dispatch({ type: 'session-absent' });
        }
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: 'session-absent' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const endSession = useCallback(() => {
    cognitoSignOut();
    setAuthToken(null);
    dispatch({ type: 'session-expired' });
  }, []);

  // One refresh in flight at a time. A screen that fires three calls at
  // once must not start three refreshes and race them.
  const refreshInFlight = useRef<Promise<string | null> | null>(null);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const attempt = (async () => {
      try {
        const session = await refreshSession();
        if (session && !isTokenExpired(session.idToken)) {
          setAuthToken(session.idToken);
          dispatch({ type: 'token-refreshed', session, nowMs: Date.now() });
          return session.idToken;
        }
      } catch {
        // Fall through to the sign-out below: a refresh that throws is as
        // unrecoverable as one that returns nothing.
      }
      endSession();
      return null;
    })();
    refreshInFlight.current = attempt;
    try {
      return await attempt;
    } finally {
      refreshInFlight.current = null;
    }
  }, [endSession]);

  useEffect(() => {
    setUnauthorizedHandler(refreshToken);
    return () => setUnauthorizedHandler(null);
  }, [refreshToken]);

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState !== 'visible') return;
      const token = state.idToken;
      if (!token || !isTokenExpired(token)) return;
      void refreshToken();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [state.idToken, refreshToken]);

  const signIn = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      const session = await cognitoSignIn(email, password);
      setAuthToken(session.idToken);
      dispatch({ type: 'signed-in', session, nowMs: Date.now() });
      return true;
    } catch (error) {
      setAuthToken(null);
      dispatch({ type: 'sign-in-failed', message: signInErrorMessage(error) });
      return false;
    }
  }, []);

  const signOut = useCallback(() => {
    cognitoSignOut();
    setAuthToken(null);
    dispatch({ type: 'signed-out' });
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ ...state, signIn, signOut }), [state, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
